import React, { useState, useEffect, useCallback, useRef } from "react";
import { Entry } from "../models/entries";
import { api } from "../api";
import { User, UserJSON } from "../models/users";
import { Compo } from "../models/compos";
import { Comment, CommentJSON } from "../models/comments";
import {
  CommentComponent,
  CommentAnonState,
} from "../DesignSystem/CommentComponent";
import { LeaveAComment, commentReplyHandle } from "../Comments/LeaveAComment";
import { VoteJSON } from "../models/vote";
import { useUser } from "../state";
import { EntryReview } from "../../../src/apidefs";
import { RLink } from "../DesignSystem/RLink";
import { EntryTitle } from "../DesignSystem/EntryTitle";
import { EntryDescription } from "../DesignSystem/EntryDescription";

const STUPID_REASONS = [
  "The people must have a sore throat.",
  "The people need to speak up.",
  "The people are uncharacteristically quiet today.",
  "The people seem to be preoccupied with Rocket League instead.",
];

function getStupid(): string {
  return STUPID_REASONS[Math.floor(Math.random() * STUPID_REASONS.length)];
}

export function EntryMainContent({
  entryId,
}: {
  entryId: number;
}): JSX.Element {
  const userState = useUser();
  const user = userState.type === "logged-in" ? userState.user : undefined;
  const entryRequestToken = useRef(0);

  const [state, setState] = useState<{
    entry: Entry | undefined;
    compo: Compo | undefined;
    judge_votes: VoteJSON[];
    has_favorited: boolean;
    comments: {
      comment: CommentJSON;
      user: UserJSON;
      likes: number;
      is_liked: boolean;
      author_banned: boolean;
    }[];
    is_author: boolean;
    entry_users: User[];
    hidden: boolean;
    reviews: EntryReview[];
    secret_santa_recipient: UserJSON | undefined;
    secret_santa_recipient_external_user_name: string | undefined;
  }>({
    entry: undefined,
    compo: undefined,
    is_author: false,
    judge_votes: [],
    entry_users: [],
    reviews: [],
    comments: [],
    hidden: false,
    has_favorited: false,
    secret_santa_recipient: undefined,
    secret_santa_recipient_external_user_name: undefined,
  });

  const loadEntry = useCallback(async (id: number): Promise<void> => {
    const requestToken = ++entryRequestToken.current;

    try {
      const result = await api.get<"/api/entry/:id">(`/api/entry/${id}`);

      if (requestToken !== entryRequestToken.current) return;
      if (api.isToast(result)) return;

      setState({
        reviews: result.reviews,
        has_favorited: result.has_favorited,
        judge_votes: result.judge_votes,
        hidden: result.entry.hidden,
        entry: new Entry(result.entry),
        entry_users: result.entry_users.map((u) => new User(u)),
        is_author: result.is_author,
        compo: new Compo(result.compo),
        comments: result.comments,
        secret_santa_recipient: result.secret_santa_recipient,
        secret_santa_recipient_external_user_name:
          result.secret_santa_recipient_external_user_name,
      });
    } catch (err) {
      console.error("Failed to load entry", err);
    }
  }, []);

  useEffect(() => {
    loadEntry(entryId);
  }, [entryId, loadEntry]);

  if (!state.entry || !state.compo) {
    return <div>Loading!</div>;
  }

  return (
    <div style={{ padding: "0 8px" }}>
      <div className="px-2">
        <EntryTitle
          entry={state.entry}
          user={user}
          hidden={state.hidden}
          hasFavorited={state.has_favorited}
          showCollabButton={state.is_author}
          entryUsers={state.entry_users}
          compoReleased={state.compo.released}
        />
      </div>
      <div>
        <div>
          <EntryDescription
            entry={state.entry}
            secretSantaRecipient={state.secret_santa_recipient}
            secretSantaRecipientExternalUserName={
              state.secret_santa_recipient_external_user_name
            }
          />

          {state.compo.state === "judging" &&
            user &&
            state.entry_users.filter((x) => x.id === user.id).length === 0 &&
            state.entry.is_judged && (
              <RLink to={`/compo/${state.entry.compo_id}/judge`}>
                Vote on this song!
              </RLink>
            )}

          {state.entry.type === "art" && (
            <div>
              <img src={state.entry.uploaded_url} />
            </div>
          )}

          {state.reviews.length > 0 && (
            <div style={{ paddingTop: "30px" }}>
              <h2 className="mb-4 flex items-center gap-3 text-2xl font-semibold text-slate-800 dark:text-white">
                <i
                  className="fa fa-star text-slate-400 dark:text-slate-500"
                  aria-hidden="true"
                ></i>
                Reviews
                <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-sm font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                  {state.reviews.length}
                </span>
              </h2>
              {state.reviews.map((review, i) => (
                <CommentComponent
                  key={i + " " + state.entry!.id}
                  anonymous={"never"}
                  replyCB={(username) => commentReplyHandle.replyTo(username)}
                  compo={state.compo!}
                  vote={
                    state.reviews[i].judge
                      ? state.judge_votes.find(
                          (x) => x.user_id === state.reviews[i].id
                        )
                      : undefined
                  }
                  comment_user={review}
                  comment_likes={state.reviews[i].review_favorites}
                  comment_is_liked={state.reviews[i].has_favorited}
                  review={review}
                />
              ))}
            </div>
          )}

          <div style={{ paddingTop: "50px" }}>
            <h2 className="mb-4 flex items-center gap-3 text-2xl font-semibold text-slate-800 dark:text-white">
              <i
                className="fa fa-comments text-slate-400 dark:text-slate-500"
                aria-hidden="true"
              ></i>
              Peanut Gallery
              <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-sm font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                {state.comments.length}
              </span>
            </h2>

            {state.comments.map((comment, i) => {
              let anonymous: CommentAnonState = "never";

              if (comment.comment.anonymous && state.compo!.released) {
                anonymous = "was anon";
              }

              if (
                comment.comment.anonymous &&
                user &&
                comment.comment.user_id === user.id &&
                !state.compo!.released
              ) {
                anonymous = "anon but yours";
              }

              return (
                <div key={state.entry!.id * 100 + i}>
                  <CommentComponent
                    anonymous={anonymous}
                    replyCB={(username) => commentReplyHandle.replyTo(username)}
                    compo={state.compo!}
                    comment_user={new User(state.comments[i].user)}
                    comment_likes={state.comments[i].likes}
                    comment_is_liked={state.comments[i].is_liked}
                    comment={new Comment(comment.comment)}
                    author_banned={state.comments[i].author_banned}
                  />
                </div>
              );
            })}

            {state.compo && state.comments.length === 0 && (
              <div className="pb-2 text-[#aaaaaa]">
                {getStupid()} Why not leave the first comment?
              </div>
            )}

            <div style={{ paddingTop: "40px" }}>
              {state.compo && state.entry && (
                <LeaveAComment
                  canPostAnonymously={
                    user &&
                    state.entry.confirmedUserIds.includes(user.id) &&
                    state.entry.anonymous &&
                    state.compo.state !== "over"
                      ? true
                      : false
                  }
                  entry={state.entry!}
                  onAddComment={(comment) => {
                    setState((old) => ({
                      ...old,
                      comments: [
                        ...old.comments,
                        {
                          comment: comment,
                          user: user!,
                          likes: 0,
                          is_liked: false,
                          author_banned: false,
                        },
                      ],
                    }));
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
